import { useStoreState } from '~/store';
import type { BaseFeatureTogglesInterface } from '~/store/navigation/BaseFeatureToggles';
import type { BaseFeaturesInterface } from '~/store/navigation/BaseFeatures';

// Create a union type of all valid feature flag keys
type FeatureToggleKeys = keyof BaseFeatureTogglesInterface | keyof BaseFeaturesInterface;

/**
 * Custom hook to check if a feature flag is enabled for the current partner.
 *
 * @param flagName - The feature flag name (must be a valid key from BaseFeatureToggles or featureToggleHelpOverride)
 * @returns boolean - true if the flag is enabled ('true' string value), false otherwise
 *
 * @example
 * ```typescript
 * import { BaseFeatureToggles } from '~/store/navigation/BaseFeatureToggles';
 * import { useFeatureFlag } from '~/hooks/useFeatureFlag';
 *
 * const MyComponent = () => {
 *   const isLimitsEnabled = useFeatureFlag(BaseFeatureToggles.LIMITS);
 *
 *   return isLimitsEnabled ? <LimitsComponent /> : <div>Limits disabled</div>;
 * };
 * ```
 */
export const useFeatureFlag = (flagName: FeatureToggleKeys): boolean => {
  // `featureToggles` is typed non-optional but is absent on real partner documents, and `a?.b[k]`
  // still throws when `b` is undefined — the store guards the same read with `|| {}`. Coerced through
  // String() rather than calling a string method on the value: these come from outside this app, and
  // a JSON boolean would throw inside the selector and take the page down, where the old strict
  // compare simply read as false.
  return useStoreState(
    (state) => String(state.partner.activePartner?.featureToggles?.[flagName] ?? '').toLowerCase() === 'true',
  );
};
